import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { fetchPlatform, fetchProviders } from './api';
import { HealthCard } from './HealthCard';
import { AiUsageSection } from './AiUsageSection';
import { CacheInsightsSection } from './CacheInsightsSection';
import { AnthropicDetailsSection } from './AnthropicDetailsSection';
import { ElevenLabsDetailsSection } from './ElevenLabsDetailsSection';
import { RevenueCatDetailsSection } from './RevenueCatDetailsSection';
import { PushDetailsSection } from './PushDetailsSection';
import type { ProviderHealth, Status } from './types';

const REFRESH_INTERVAL_MS = 60_000;

export function HealthPage(): JSX.Element {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const queryClient = useQueryClient();

  const providers = useQuery({
    queryKey: ['health', 'providers'],
    queryFn: () => fetchProviders(),
    refetchInterval: autoRefresh ? REFRESH_INTERVAL_MS : false,
  });

  const platform = useQuery({
    queryKey: ['health', 'platform'],
    queryFn: () => fetchPlatform(),
    refetchInterval: autoRefresh ? REFRESH_INTERVAL_MS : false,
  });

  async function forceRefresh() {
    // Server-Cache umgehen + Query-Cache invalidieren, damit beide Karten-Grids
    // wirklich frische Daten anzeigen.
    queryClient.setQueryData(['health', 'providers'], await fetchProviders({ force: true }));
    queryClient.setQueryData(['health', 'platform'], await fetchPlatform({ force: true }));
  }

  // Pause auto-refresh when tab is hidden, resume on visible.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        providers.refetch();
        platform.refetch();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [providers, platform]);

  const generatedAt = providers.data?.generatedAt ?? platform.data?.generatedAt;
  const isLoading = providers.isLoading || platform.isLoading;
  const isFetching = providers.isFetching || platform.isFetching;

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">System-Health</h1>
          <p className="mt-1 text-xs text-text-tertiary">
            {generatedAt ? `Letzter Refresh: ${formatRelative(generatedAt)}` : 'Lädt …'}
            {isFetching && !isLoading && <span className="ml-2 text-accent-light">aktualisiert …</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-text-secondary">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-Refresh (60 s)
          </label>
          <button
            type="button"
            onClick={forceRefresh}
            disabled={isFetching}
            className="rounded-md border px-3 py-1.5 text-xs text-text-primary hover:bg-bg-elevated disabled:opacity-50"
            style={{ borderColor: darkTheme.colors.borderSubtle }}
          >
            ↻ Refresh
          </button>
        </div>
      </header>

      {generatedAt && (
        <StatusBanner providers={providers.data?.providers ?? []} platform={platform.data?.sections ?? []} />
      )}

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          Externe Dienste
        </h2>
        {providers.error ? (
          <ErrorBox message="Provider-Daten konnten nicht geladen werden." error={providers.error} />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {providers.data?.providers.map((p) => (
              <HealthCard key={p.name} data={p} />
            ))}
            {isLoading && Array.from({ length: 7 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          Plattform
        </h2>
        {platform.error ? (
          <ErrorBox message="Plattform-Daten konnten nicht geladen werden." error={platform.error} />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {platform.data?.sections.map((p) => (
              <HealthCard key={p.name} data={p} />
            ))}
            {isLoading && Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          KI-Prompt-Cache — Trefferquote, Kosten &amp; Ersparnis
        </h2>
        <CacheInsightsSection />
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          Anthropic — Verbrauch &amp; Forecast
        </h2>
        <AnthropicDetailsSection />
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          ElevenLabs — Verbrauch &amp; Trend
        </h2>
        <ElevenLabsDetailsSection />
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          RevenueCat — Umsatz &amp; Subscriptions
        </h2>
        <RevenueCatDetailsSection />
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          Push-Notifications — Sends, Skips &amp; Failure-Rate
        </h2>
        <PushDetailsSection />
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          KI-Auslastung — Verteilung pro User (letzte 30 Tage)
        </h2>
        <AiUsageSection />
      </section>

      <p className="text-[11px] text-text-tertiary">
        Provider-Probes 60 s gecacht · Plattform-Probes 30 s · KI-Auslastung wird nightly aggregiert
      </p>
    </div>
  );
}

function StatusBanner({ providers, platform }: { providers: ProviderHealth[]; platform: ProviderHealth[] }): JSX.Element | null {
  const all = [...providers, ...platform];
  if (all.length === 0) return null;

  const down = all.filter((p) => p.status === 'down');
  const degraded = all.filter((p) => p.status === 'degraded');
  const unknown = all.filter((p) => p.status === 'unknown');

  let label: string;
  let color: string;
  let bg: string;
  let status: Status;

  if (down.length > 0) {
    status = 'down';
    label = `${down.length} ${down.length === 1 ? 'Bereich' : 'Bereiche'} down: ${down.map((p) => p.displayName).join(', ')}`;
    color = darkTheme.colors.danger;
    bg = darkTheme.colors.dangerBg;
  } else if (degraded.length > 0) {
    status = 'degraded';
    label = `${degraded.length} ${degraded.length === 1 ? 'Bereich' : 'Bereiche'} mit Warnung`;
    color = darkTheme.colors.warning;
    bg = darkTheme.colors.warningBg;
  } else if (unknown.length === all.length) {
    status = 'unknown';
    label = 'Keine Daten verfügbar';
    color = darkTheme.colors.textTertiary;
    bg = darkTheme.colors.bgElevated;
  } else {
    status = 'up';
    label = 'Alle Systeme normal';
    color = darkTheme.colors.success;
    bg = darkTheme.colors.successBg;
  }

  return (
    <div
      role="status"
      className="mb-6 flex items-center gap-3 rounded-md px-4 py-2.5 text-sm font-medium"
      style={{ background: bg, color }}
      data-status={status}
    >
      <span aria-hidden>●</span>
      <span>{label}</span>
    </div>
  );
}

function SkeletonCard(): JSX.Element {
  return (
    <div
      className="animate-pulse rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
        minHeight: 220,
      }}
    >
      <div className="mb-3 h-4 w-32 rounded bg-bg-elevated" />
      <div className="space-y-2">
        <div className="h-3 w-full rounded bg-bg-elevated" />
        <div className="h-3 w-3/4 rounded bg-bg-elevated" />
        <div className="h-3 w-1/2 rounded bg-bg-elevated" />
      </div>
    </div>
  );
}

function ErrorBox({ message, error }: { message: string; error: unknown }): JSX.Element {
  return (
    <div
      className="rounded-md p-4 font-mono text-xs"
      style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
    >
      <p className="mb-1 font-sans font-semibold">{message}</p>
      <p>{error instanceof Error ? error.message : String(error)}</p>
    </div>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `vor ${Math.floor(ms / 1000)} s`;
  if (ms < 3_600_000) return `vor ${Math.floor(ms / 60_000)} min`;
  return `vor ${Math.floor(ms / 3_600_000)} h`;
}
